import type { Order } from '@/lib/redux/features/booking/type'

const DB_NAME = 'traveleasy-pwa'
const DB_VERSION = 1
const STORE_NAME = 'booking-snapshots'
const BOOKING_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000
const OFFLINE_PRODUCT_TYPES = new Set(['FLIGHT', 'HOTEL'])

export type BookingSnapshotRecord = {
  key: string
  orderCode: string
  productType: string
  data: Order
  savedAt: number
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined'
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(new Error('Could not open booking snapshot database'))
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
  })
}

function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode)
        const store = transaction.objectStore(STORE_NAME)
        const request = fn(store)

        request.onsuccess = () => resolve(request.result as T)
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
        transaction.oncomplete = () => db.close()
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'))
      }),
  )
}

export function getBookingSnapshotKey(orderCode: string, phoneNumber?: string | null) {
  const code = String(orderCode || '').trim()
  const phone = String(phoneNumber || '').trim()
  return phone ? `${code}:${phone}` : code
}

export function shouldPersistBookingSnapshot(order: Order | null | undefined) {
  return !!order?.orderCode && OFFLINE_PRODUCT_TYPES.has(order.productType)
}

export async function saveBookingSnapshot(
  orderCode: string,
  data: Order,
  phoneNumber?: string | null,
): Promise<void> {
  if (!isBrowser() || !shouldPersistBookingSnapshot(data)) {
    return
  }

  const key = getBookingSnapshotKey(orderCode, phoneNumber)
  const record: BookingSnapshotRecord = {
    key,
    orderCode: data.orderCode,
    productType: data.productType,
    data,
    savedAt: Date.now(),
  }

  await withStore('readwrite', (store) => store.put(record))
}

export async function getBookingSnapshot(
  orderCode: string,
  phoneNumber?: string | null,
): Promise<BookingSnapshotRecord | null> {
  if (!isBrowser()) {
    return null
  }

  const key = getBookingSnapshotKey(orderCode, phoneNumber)
  const record = await withStore<BookingSnapshotRecord | undefined>('readonly', (store) => store.get(key))

  if (!record) {
    return null
  }

  if (Date.now() - record.savedAt > BOOKING_SNAPSHOT_TTL_MS) {
    await withStore('readwrite', (store) => store.delete(key))
    return null
  }

  return record
}
